import * as React from "react";
import apiClient from "api";
import { gettext } from "i18n";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import { useQuery } from "@apollo/client";
import PageHeader from "theme/components/common/PageHeader";
import { sortByKey } from "utils/formatting";
import Table from "react-bootstrap/Table";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Flex from "theme/components/common/Flex";
import { useAuth, isOrgAdmin, isAnyOrgAdmin } from "state/auth";
import { GET_REPORTING_DASHBOARDS } from "utils/stations";
import {
  GetReportingDashboards,
  GetReportingDashboards_reporting_dashboard,
} from "utils/__generated__/GetReportingDashboards";

const ReportingDashboardListPage = () => {
  const { user } = useAuth();
  const { data, refetch } = useQuery<GetReportingDashboards>(
    GET_REPORTING_DASHBOARDS,
  );
  const isGlobalAdmin = !!user?.admin;
  const anyOrgAdmin = isAnyOrgAdmin(user);

  const dashboards = data?.reporting_dashboard ?? [];

  const onDeleteDashboard = (id: string) => {
    if (
      !window.confirm(
        gettext("Are you sure? This action is permanent and cannot be undone."),
      )
    ) {
      return;
    }

    apiClient
      .request("/admin/delete-reporting-dashboard", {
        method: "POST",
        data: { id },
      })
      .then(async () => {
        toast.success(gettext("Reporting dashboard deleted successfully"));
        refetch();
      })
      .catch((e) => {
        console.error(e);
        toast.error(gettext(e.toString()));
      });
  };

  const hasActionColumn =
    isGlobalAdmin ||
    dashboards.some((d: any) => isOrgAdmin(user, d.organization_id));

  return (
    <div>
      <PageHeader title="Reporting Dashboards" className="mb-3" />

      {(isGlobalAdmin || anyOrgAdmin) && (
        <Row className="g-3 mb-3">
          <Col>
            <Card>
              <Card.Body>
                <Row className="align-items-center">
                  <Col xs={8} sm={6}>
                    <strong>Reporting dashboard administration</strong>
                  </Col>
                  <Col xs={4} sm={6}>
                    <Flex justifyContent="end">
                      <Button
                        as={Link as any}
                        to="/reporting-dashboards/admin/add"
                        variant="outline-primary"
                        size="sm"
                      >
                        Add
                      </Button>
                    </Flex>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {dashboards.length === 0 ? (
        <Card>
          <Card.Body>
            <p className="text-muted mb-0">
              {gettext("No reporting dashboards available.")}
            </p>
          </Card.Body>
        </Card>
      ) : (
        <>
          <Card className="mb-3 d-none d-sm-block">
            <Card.Body>
              <Table className="mb-0">
                <thead>
                  <tr>
                    <th>{gettext("Name")}</th>
                    <th>{gettext("Organization")}</th>
                    <th>{gettext("Last updated")}</th>
                    {hasActionColumn && <th></th>}
                  </tr>
                </thead>
                <tbody>
                  {sortByKey(dashboards, "name").map((dashboard) => (
                    <tr key={dashboard.id}>
                      <td>
                        <Link to={`/reporting-dashboards/${dashboard.id}`}>
                          {dashboard.name}
                        </Link>
                      </td>
                      <td>
                        {dashboard.organization?.name ??
                          gettext("Global administrators")}
                      </td>
                      <td>
                        {dashboard.updated_at
                          ? new Date(dashboard.updated_at).toLocaleDateString()
                          : "-"}
                      </td>
                      {(isGlobalAdmin ||
                        isOrgAdmin(user, dashboard.organization_id)) && (
                        <td>
                          <Flex justifyContent="end">
                            <div className="dashboard-row-buttons">
                              <Button
                                as={Link as any}
                                to={`/reporting-dashboards/admin/edit/${dashboard.id}`}
                                variant="outline-primary"
                                size="sm"
                              >
                                Edit
                              </Button>
                              {isGlobalAdmin && (
                                <Button
                                  variant="danger"
                                  size="sm"
                                  onClick={() =>
                                    onDeleteDashboard(dashboard.id)
                                  }
                                >
                                  {gettext("Delete")}
                                </Button>
                              )}
                            </div>
                          </Flex>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          <Row className="g-2 d-sm-none mb-3">
            {sortByKey(dashboards, "name").map((dashboard) => {
              const canEdit =
                isGlobalAdmin || isOrgAdmin(user, dashboard.organization_id);
              return (
                <Col xs={12} key={dashboard.id}>
                  <Card>
                    <Card.Body className="py-2 px-3">
                      <div className="d-flex align-items-center justify-content-between gap-2">
                        <div
                          className="d-flex flex-column"
                          style={{ minWidth: 0 }}
                        >
                          <Link
                            to={`/reporting-dashboards/${dashboard.id}`}
                            className="fw-semibold text-truncate"
                          >
                            {dashboard.name}
                          </Link>
                          {dashboard.organization?.name && (
                            <small className="text-muted text-truncate">
                              {dashboard.organization.name}
                            </small>
                          )}
                          {dashboard.updated_at && (
                            <small className="text-muted">
                              {new Date(
                                dashboard.updated_at,
                              ).toLocaleDateString()}
                            </small>
                          )}
                        </div>
                        {canEdit && (
                          <div className="dashboard-row-buttons flex-shrink-0">
                            <Button
                              as={Link as any}
                              to={`/reporting-dashboards/admin/edit/${dashboard.id}`}
                              variant="outline-primary"
                              size="sm"
                            >
                              Edit
                            </Button>
                            {isGlobalAdmin && (
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => onDeleteDashboard(dashboard.id)}
                              >
                                {gettext("Delete")}
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </>
      )}
    </div>
  );
};

export default ReportingDashboardListPage;
